import React, { useContext } from "react";
import { useInRouterContext } from "react-router-dom";
import { NotificationBell } from "../layout/NotificationBell";
import { AuthContext } from "../../context/AuthContext";

const ROLE_STYLES = {
  super_admin: "bg-indigo-100 text-indigo-800 border-indigo-200",
  owner_admin: "bg-slate-100 text-slate-800 border-slate-200",
  manager_trainer: "bg-amber-100 text-amber-800 border-amber-200",
  representative: "bg-emerald-100 text-emerald-800 border-emerald-200",
  viewer: "bg-zinc-100 text-zinc-600 border-zinc-200",
};
const LIFECYCLE_STYLES = {
  prospect: "bg-sky-50 text-sky-700",
  active_lead: "bg-blue-50 text-blue-700",
  qualified_lead: "bg-indigo-50 text-indigo-700",
  client: "bg-emerald-50 text-emerald-700",
  referral_source: "bg-violet-50 text-violet-700",
  partner: "bg-fuchsia-50 text-fuchsia-700",
  inactive: "bg-slate-50 text-slate-600",
  disqualified: "bg-rose-50 text-rose-700",
};
const STAGE_STYLES = {
  prospect_identified: "text-sky-700 border-sky-300 bg-sky-50",
  initial_contact: "text-blue-700 border-blue-300 bg-blue-50",
  qualified_opportunity: "text-indigo-700 border-indigo-300 bg-indigo-50",
  discovery_complete: "text-violet-700 border-violet-300 bg-violet-50",
  solution_presented: "text-fuchsia-700 border-fuchsia-300 bg-fuchsia-50",
  decision_pending: "text-amber-700 border-amber-300 bg-amber-50",
  closed_won: "text-emerald-700 border-emerald-300 bg-emerald-50",
  closed_lost: "text-rose-700 border-rose-300 bg-rose-50",
};
const STATUS_STYLES = {
  open: "bg-blue-100 text-blue-700",
  acknowledged: "bg-indigo-100 text-indigo-700",
  in_progress: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

export function Pill({ value, type = "lifecycle", testId }: { value: string; type?: string; testId?: string }) {
  const styles = {
    lifecycle: LIFECYCLE_STYLES,
    role: ROLE_STYLES,
    stage: STAGE_STYLES,
    status: STATUS_STYLES,
  }[type];
  const cls = (styles && styles[value]) || "bg-zinc-100 text-zinc-700";
  const label = (value || "").replace(/_/g, " ");
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${cls}`}
      data-testid={testId}
    >
      {label}
    </span>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: React.ReactNode; subtitle?: React.ReactNode; actions?: React.ReactNode }) {
  const auth = useContext(AuthContext);
  const isInRouter = useInRouterContext();
  return (
    <div className="border-b border-slate-200 bg-white sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-5 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-slate-900" style={{ fontFamily: "Manrope, ui-sans-serif" }}>
            {title}
          </h1>
          {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">{actions}{auth && isInRouter && <NotificationBell />}</div>
      </div>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action }: { icon?: any; title: React.ReactNode; description?: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="text-center py-16 px-4" data-testid="empty-state">
      {Icon && <Icon className="w-8 h-8 mx-auto text-slate-400 mb-3" />}
      <h3 className="text-sm font-medium text-slate-800">{title}</h3>
      {description && <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
